import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { ConfigProvider } from '@/app/ConfigProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import LoginPage from '@/pages/Login';
import DashboardPage from '@/pages/Dashboard';
import ProjectPage from '@/pages/Project';
import DialogPage from '@/pages/Dialog';
import PrivacyPage from '@/pages/Privacy';
import NotFoundPage from '@/pages/NotFound';
import { captureError, ErrorBoundary } from '@/lib/error-tracking';
import { toast } from '@/hooks/use-toast';

// The stale-data notice, held as a single handle. It persists rather than
// auto-dismissing because the condition it reports (you are looking at data we
// couldn't refresh) lasts until a fetch succeeds, not five seconds. Reusing one
// handle means repeated failures don't stack identical toasts, and clearing it
// on the next success stops it outliving the condition — a stuck "this is stale"
// on fresh data would be the same lie the error states were added to remove.
//
// Liveness is checked via isActive() rather than just "is the handle set",
// because a persisted toast is precisely the one a user will click the X on, and
// TOAST_LIMIT can evict it too. Either would leave the handle looking live and
// suppress the notice for the rest of the session while the data stayed stale.
// Re-raising on the next failure is the right call even after a manual dismiss:
// a new failure is new information, and the alternative is going silent.
//
// Any successful query clears it, not just the one that failed. During a
// connectivity blip queries fail and recover together, so this is accurate in
// practice, and it self-corrects: if the failed query is still broken, its next
// refetch puts the notice straight back.
let staleNotice: ReturnType<typeof toast> | null = null;

// Queries and mutations catch everything their fns throw (including stream
// errors), so window-level handlers never see them — this is the one place
// all thrown data-layer errors pass through.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureError(error, { source: 'query' });
      // A failed first load has nothing to show, so the view renders its own
      // blocking error. A failed *refetch* keeps the last good data on screen,
      // which would otherwise be invisible — the user reads stale state as
      // current. That's the likelier case of the two (focus refetch past
      // staleTime, or a mutation's invalidate), so say so once here rather than
      // in every consumer. Fires once per error episode, not per retry.
      if (query.state.data !== undefined && !staleNotice?.isActive()) {
        staleNotice = toast({
          variant: 'destructive',
          persist: true,
          title: "Couldn't refresh",
          description: "You're seeing the last version that loaded. Check your connection.",
        });
      }
    },
    onSuccess: () => {
      staleNotice?.dismiss();
      staleNotice = null;
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => captureError(error, { source: 'mutation' }),
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function ErrorFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <p className="text-destructive">Something went wrong.</p>
      <button className="text-sm underline" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
              <ErrorBoundary fallback={<ErrorFallback />}>
                <Routes>
                  <Route path="/" element={<LoginPage />} />
                  <Route path="/dialog/:token" element={<DialogPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route element={<AppLayout />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/project/:projectId" element={<ProjectPage />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </ErrorBoundary>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
